import AuthUtils from "@/providers/firebase/authUtils";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export interface IndexesPayload {
  bot_index_dimensions: Record<string, unknown>;
  kernel_index_dimensions: Record<string, unknown>;
  entity_index_dimensions: Record<string, unknown>;
  process_index_dimensions: Record<string, unknown>;
}

export type IndexSource = "cache" | "network";

export interface IndexesResult {
  indexes: IndexesPayload;
  source: IndexSource;
}

/**
 * Shape used before any index has been loaded. There is deliberately NO bundled
 * JSON fallback: the DB kernels behind /api/v1/config/indexes are the only source
 * of truth, so a stale copy compiled into the bundle would silently mask a broken
 * fetch (see PROJECT-076/MR14). Consumers render their loading state instead.
 */
export const EMPTY_INDEXES: IndexesPayload = Object.freeze({
  bot_index_dimensions: {},
  kernel_index_dimensions: {},
  entity_index_dimensions: {},
  process_index_dimensions: {},
});

// Bump the suffix whenever the cached shape changes — old entries are then ignored
// rather than fed to consumers that no longer understand them.
const CACHE_KEY = "raisy.config.indexes.v1";
/** How long a persisted copy may be served without hitting the network. */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CacheEnvelope {
  savedAt: number;
  data: IndexesPayload;
}

let memoryIndexes: IndexesPayload | null = null;
let memorySavedAt = 0;
let inFlight: Promise<IndexesResult> | null = null;
/** Guards the app-start revalidation so it costs at most one request per page load. */
let revalidated = false;
/** Serialised copy of `memoryIndexes`, kept so a revalidation that changes nothing skips the re-render. */
let memorySerialised = "";

type Listener = (indexes: IndexesPayload) => void;
const listeners = new Set<Listener>();

/** An index payload is only worth caching if at least one index actually has content. */
function hasContent(data: unknown): data is IndexesPayload {
  if (!data || typeof data !== "object") return false;
  return Object.values(data as Record<string, unknown>).some(
    (v) => v && typeof v === "object" && Object.keys(v as object).length > 0,
  );
}

/**
 * Hydrate the in-memory copy from localStorage. Runs synchronously at module load
 * so the very first render of any consumer already has data — no spinner, no
 * network round-trip on the critical path for a returning user.
 */
function hydrateFromStorage(): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const envelope = JSON.parse(raw) as CacheEnvelope;
    if (!hasContent(envelope?.data)) return;
    memoryIndexes = { ...EMPTY_INDEXES, ...envelope.data };
    memorySavedAt = typeof envelope.savedAt === "number" ? envelope.savedAt : 0;
    memorySerialised = JSON.stringify(memoryIndexes);
  } catch {
    // Corrupt or unavailable storage is not fatal — we just fetch from the API.
  }
}

hydrateFromStorage();

function persist(data: IndexesPayload): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data } satisfies CacheEnvelope));
  } catch {
    // Quota/private-mode failures only cost us the fast path on the next load.
  }
}

function commit(data: IndexesPayload): IndexesPayload {
  const merged = { ...EMPTY_INDEXES, ...data };
  const serialised = JSON.stringify(merged);
  memorySavedAt = Date.now();
  persist(merged);
  if (serialised === memorySerialised && memoryIndexes) return memoryIndexes;
  memoryIndexes = merged;
  memorySerialised = serialised;
  for (const listener of listeners) listener(merged);
  return merged;
}

/** The cached indexes, however old, or null if nothing has ever been loaded. */
export function getCachedIndexes(): IndexesPayload | null {
  return memoryIndexes;
}

/** True when a cached copy exists and is young enough to serve without a network call. */
export function isCacheFresh(): boolean {
  return !!memoryIndexes && Date.now() - memorySavedAt < CACHE_TTL_MS;
}

/** Notified whenever a network response changes the cached indexes. Returns an unsubscribe fn. */
export function subscribeIndexes(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function requestIndexes(): Promise<IndexesPayload> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v1/config/indexes`, { headers });
  if (!res.ok) {
    throw new Error(`Failed to load indexes: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();
  if (!json.status || !json.data) {
    throw new Error(json.message || "Invalid indexes response");
  }
  if (!hasContent(json.data)) {
    throw new Error("Indexes response contained no dimension indexes");
  }
  return json.data as IndexesPayload;
}

/**
 * Load the four canonical dimension indexes, cache-first.
 *
 * - A fresh cached copy is returned without touching the network.
 * - `force` always goes to the API (used by the app-start revalidation).
 * - Concurrent callers share one in-flight request.
 * - If the request fails but a stale cached copy exists, that copy is returned
 *   rather than throwing — the alternative is an empty UI.
 */
export async function loadIndexes(options?: { force?: boolean }): Promise<IndexesResult> {
  if (!options?.force && isCacheFresh()) {
    return { indexes: memoryIndexes!, source: "cache" };
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const data = await requestIndexes();
      return { indexes: commit(data), source: "network" as const };
    } catch (e) {
      if (memoryIndexes) {
        console.warn("[configApi] Index refresh failed, serving cached copy:", e);
        return { indexes: memoryIndexes, source: "cache" as const };
      }
      throw e;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Cache-first index payload. Thin wrapper over {@link loadIndexes} for callers that ignore the source. */
export async function fetchIndexes(): Promise<IndexesPayload> {
  const { indexes } = await loadIndexes();
  return indexes;
}

/**
 * Warm the cache as early as possible, off the render path.
 *
 * Called from `main.tsx` before React mounts: a returning user already has a token
 * in localStorage, so the request goes out in parallel with the first paint while
 * the UI renders instantly from the persisted copy. Always revalidates (once per
 * page load) so a DB kernel change lands without waiting out the TTL. Never throws.
 */
export function prefetchIndexes(): void {
  if (revalidated || !AuthUtils.hasValidToken()) return;
  revalidated = true;
  void loadIndexes({ force: true })
    // A forced load that still reports "cache" means the request failed and the stale
    // copy was served — clear the guard so a later trigger (post-login) can retry.
    .then(({ source }) => {
      if (source === "cache") revalidated = false;
    })
    .catch(() => {
      // Silent: consumers surface their own error state when they need the data.
      revalidated = false;
    });
}

export function clearIndexCache(): void {
  memoryIndexes = null;
  memorySavedAt = 0;
  memorySerialised = "";
  inFlight = null;
  revalidated = false;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Nothing to do — the in-memory copy is already gone.
  }
}
